import React, { useEffect, useState } from "react";
import { Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import MenuLateralAdmin from "../MenuLateralAdmin/MenuLateralAdmin";
import HeaderAdmin from "../Users/HeaderAdmin";
import Users from "../Users/Users";
import Content from "../Content/AdminContent";
import ReportsPage from "../Reports/ReportsPage";
import ChargebackReport from "../Reports/ChargebackReport";
import Settings from "../Settings/Settings";
import DiscountCampaignModal from "../Users/DiscountCampaignModal";
import AccessHoldQueueModal from "../Users/AccessHoldQueueModal";
import WebhookEmailCenter from "../Email/WebhookEmailCenter";
import PaymentOperationsDashboard from "../Operations/PaymentOperationsDashboard";

const ADMIN_VIEWS = new Set([
  "usuarios",
  "conteudo",
  "relatorios",
  "vendas_pagamentos",
  "chargebacks",
  "configuracoes",
  "email_webhooks",
]);

function getViewFromUrl() {
  const requestedView = new URLSearchParams(window.location.search).get("view");
  return ADMIN_VIEWS.has(requestedView) ? requestedView : null;
}

function getInitialAdminView() {
  return getViewFromUrl() || "usuarios";
}

const Admin = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [openDiscountCampaign, setOpenDiscountCampaign] = useState(false);
  const [openAccessHoldQueue, setOpenAccessHoldQueue] = useState(false);
  const [usersListLoading, setUsersListLoading] = useState(false);

  const [currentView, setCurrentView] = useState(getInitialAdminView);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("DESC");
  const [monthFilter, setMonthFilter] = useState("all");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const syncViewFromUrl = () => {
      const nextView = getViewFromUrl();
      if (nextView) {
        setCurrentView((current) => (current === nextView ? current : nextView));
      }
    };

    window.addEventListener("popstate", syncViewFromUrl);
    window.addEventListener("focus", syncViewFromUrl);
    document.addEventListener("visibilitychange", syncViewFromUrl);
    syncViewFromUrl();

    return () => {
      window.removeEventListener("popstate", syncViewFromUrl);
      window.removeEventListener("focus", syncViewFromUrl);
      document.removeEventListener("visibilitychange", syncViewFromUrl);
    };
  }, []);

   const handleSearch = (searchText) => {
    setSearchQuery(searchText);
  };

  const renderContent = () => {
    switch (currentView) {
      case "usuarios":
        return (
          <Users
            searchQuery={searchQuery}
            filter={filter}
            setFilter={setFilter}
            orderFilter={orderFilter}
            setOrderFilter={setOrderFilter}
            monthFilter={monthFilter}
            refreshTrigger={refreshTrigger}
            onLoadingChange={setUsersListLoading}
          />
        );
      case "conteudo":
        return <Content />;
      case "relatorios":
        return <ReportsPage />;
      case "vendas_pagamentos":
        return <PaymentOperationsDashboard />;
      case "chargebacks":
        return <ChargebackReport />;
      case "configuracoes":
        return <Settings />;
      case "email_webhooks":
        return <WebhookEmailCenter />;
      default:
        return (
          <Users
            searchQuery={searchQuery}
            filter={filter}
            setFilter={setFilter}
            orderFilter={orderFilter}
            setOrderFilter={setOrderFilter}
            monthFilter={monthFilter}
            refreshTrigger={refreshTrigger}
            onLoadingChange={setUsersListLoading}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {isMobile ? (
        <>
          <Drawer
            anchor="left"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: "80vw",
                maxWidth: "340px",
                minWidth: "260px",
                height: "100vh",
                overflowY: "auto",
                p: 0,
                m: 0,
                bgcolor: "transparent",
                borderRadius: 0,
                boxShadow: "none",
              },
            }}
          >
            <MenuLateralAdmin
              currentView={currentView}
              setCurrentView={(view) => {
                setCurrentView(view);
                setIsDrawerOpen(false);
              }}
              handleClose={() => setIsDrawerOpen(false)}
            />
          </Drawer>
        </>
      ) : (
        <Box
          sx={{
            width: "280px",
            flexShrink: 0,
            bgcolor: "#1B263B",
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <MenuLateralAdmin currentView={currentView} setCurrentView={setCurrentView} />
        </Box>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          px: isMobile ? 0 : 2,
        }}
      >
        <HeaderAdmin
          currentView={currentView}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          filter={filter}
          setFilter={setFilter}
          orderFilter={orderFilter}
          setOrderFilter={setOrderFilter}
          monthFilter={monthFilter}
          setMonthFilter={setMonthFilter}
          onOpenDiscountCampaign={() => setOpenDiscountCampaign(true)}
          onOpenAccessHoldQueue={() => setOpenAccessHoldQueue(true)}
          onRefresh={() => setRefreshTrigger((prev) => !prev)}
          onOpenMenu={isMobile ? () => setIsDrawerOpen(true) : undefined}
          loading={currentView === "usuarios" && usersListLoading}
        />
        <Box sx={{ flexGrow: 1, pt: 2 }}>{renderContent()}</Box>
      </Box>      
      <DiscountCampaignModal
        open={openDiscountCampaign}
        onClose={() => setOpenDiscountCampaign(false)}
        onSent={() => setRefreshTrigger((prev) => !prev)}
      />
      <AccessHoldQueueModal
        open={openAccessHoldQueue}
        onClose={() => setOpenAccessHoldQueue(false)}
        onUserDeleted={() => setRefreshTrigger((prev) => !prev)}
      />
    </Box>
  );
};

export default Admin;
